// composables/useAutoFocus.js — as the fullstack's (base-layer/app/composables/useAutoFocus.js),
// same name and behaviour: focus targetRef's element once it's there, retried once if focus
// doesn't stick. Frontend addition (2026-10-14): isActive, a getter, for a form mounted inside a
// dialog that's always in the page (e.g. Sign-In): each time it turns true (the dialog opening),
// focus again.
import {nextTick, onMounted, ref, watch} from "vue";
import {useFocus} from "@vueuse/core";
import {useLog} from "@/composables/useLog";

const log = useLog("useAutoFocus");

function getActiveElementSummary() {
  const element = document?.activeElement;
  if (!element) return "none";
  const tag = element.tagName || "";
  const id = element.id || "";
  const name = element.getAttribute?.("name") || "";
  const ariaLabel = element.getAttribute?.("aria-label") || "";
  const text = (element.textContent || "").trim().slice(0, 60);
  return `tag=${tag} id=${id} name=${name} aria=${ariaLabel} text=${text}`;
}

function logActiveElement(prefix) {
  log(`${prefix}, activeElement ${getActiveElementSummary()}`);
}

function createFocusRunner(targetRef) {
  const hasFocused = ref(false);
  const {focused} = useFocus(targetRef);
  async function applyFocus() {
    const element = targetRef?.value;
    if (!element) return false;
    await nextTick();
    focused.value = true;
    return document.activeElement === element;
  }
  async function tryFocus() {
    if (hasFocused.value) return;
    const didStick = await applyFocus();
    logActiveElement("after focus");
    if (didStick) {
      hasFocused.value = true;
      return;
    }
    setTimeout(async () => {
      if (hasFocused.value) return;
      const stuckNow = await applyFocus();
      logActiveElement("after refocus");
      if (stuckNow) hasFocused.value = true;
    }, 50);
  }
  function reset() {
    hasFocused.value = false;
  }
  return {tryFocus, reset};
}

export function useAutoFocus(targetRef, isActive) {
  log(`called`);
  const {tryFocus, reset} = createFocusRunner(targetRef);
  onMounted(() => tryFocus());
  watch(() => targetRef?.value, () => tryFocus(), {flush: "post"});
  if (isActive) {
    watch(isActive, (isNowActive) => {
      if (!isNowActive) return;
      reset();
      tryFocus();
    }, {flush: "post"});
  }
  tryFocus();
}
